// CivsPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Save, X, Eye, AlertTriangle } from 'lucide-react';
import { fetchCivs, addCiv, updateCiv, removeCiv } from '../services/frontendAPI';

/* ---------- Aurora Dark theme tokens ---------- */
const AURORA_BG = {
    backgroundColor: "#0b1220",
    backgroundImage: `
    radial-gradient(1200px 800px at -20% -10%, rgba(14,165,233,.25), transparent 60%),
    radial-gradient(1200px 800px at 120% 0%, rgba(34,211,238,.22), transparent 55%),
    radial-gradient(1000px 800px at 110% 120%, rgba(168,85,247,.18), transparent 55%),
    radial-gradient(800px 600px at 0% 120%, rgba(16,185,129,.16), transparent 55%)
  `,
};
const CARD_CLASS =
    "rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)]";

/* ---------- UI primitives ---------- */
const Input = (props) => (
    <input
        {...props}
        className={
            "appearance-none px-3 py-2 text-sm font-medium border w-full rounded-xl " +
            "bg-white/5 text-slate-100 border-white/10 " +
            "placeholder:text-slate-400 " +
            "focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-300/50 " +
            (props.className || "")
        }
    />
);

const Textarea = (props) => (
    <textarea
        {...props}
        className={
            "appearance-none px-3 py-2 text-sm w-full resize-none border rounded-xl " +
            "bg-white/5 text-slate-100 border-white/10 placeholder:text-slate-400 " +
            "focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-300/50 " +
            (props.className || "")
        }
    />
);

const Button = ({ children, onClick, className = "", type="button", disabled }) => (
    <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={
            "text-sm px-3 py-2 rounded-xl border font-semibold transition-colors " +
            "bg-white/5 border-white/10 text-slate-100 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed " +
            className
        }
    >
        {children}
    </button>
);

/* ---------- page ---------- */
const CivsPage = () => {
    const [civs, setCivs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [editingCiv, setEditingCiv] = useState(null);
    const [viewingCiv, setViewingCiv] = useState(null);

    const [formData, setFormData] = useState({ name: '', data: '' });

    // Load civs on component mount
    useEffect(() => {
        loadCivs();
    }, []);

    const loadCivs = async () => {
        try {
            setLoading(true);
            const data = await fetchCivs();
            setCivs(data);
            setError(null);
        } catch (err) {
            setError('Failed to load civilizations');
            console.error('Error loading civs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            let parsedData;
            try {
                parsedData = JSON.parse(formData.data);
            } catch (err) {
                setError('Invalid JSON format in data field');
                return;
            }

            const civData = {
                name: formData.name,
                data: parsedData
            };

            if (editingCiv) {
                await updateCiv(editingCiv.id, civData);
                setSuccess('Civilization updated successfully.');
            } else {
                await addCiv(civData);
                setSuccess('Civilization created successfully.');
            }

            await loadCivs();
            resetForm();
        } catch (err) {
            setError('Failed to save civilization');
            console.error('Error saving civ:', err);
        }
    };

    const handleEdit = (civ) => {
        setEditingCiv(civ);
        setFormData({
            name: civ.name,
            data: JSON.stringify(civ.data, null, 2)
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this civilization?')) return;
        setError(null);
        setSuccess(null);
        try {
            await removeCiv(id);
            await loadCivs();
            setSuccess('Civilization deleted.');
        } catch (err) {
            setError('Failed to delete civilization');
            console.error('Error deleting civ:', err);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', data: '' });
        setEditingCiv(null);
        setShowForm(false);
    };

    const handleView = (civ) => setViewingCiv(civ);

    /* ---------- render ---------- */
    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center text-slate-200" style={AURORA_BG}>
                <div className={`${CARD_CLASS} px-6 py-5`}>
                    <div className="text-lg font-semibold mb-1">Loading civilizations…</div>
                    <div className="text-slate-400">Fetching data from database</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-slate-200" style={AURORA_BG}>
            {/* Header */}
            <header className="sticky top-0 z-30">
                <div
                    className="border-b border-white/10 backdrop-blur-md"
                    style={{ background: "linear-gradient(to right, rgba(15,23,42,.65), rgba(15,23,42,.45))" }}
                >
                    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
                        <div className="flex items-center justify-between">
                            <div className="text-xl font-extrabold tracking-tight">Civilizations Management</div>
                            <Button onClick={() => { setShowForm(true); setEditingCiv(null); }}>
                                <Plus className="w-4 h-4 mr-1 inline" />
                                Add New
                            </Button>
                        </div>
                        <div className="text-slate-400 text-sm">Manage Age of Empires IV civilization data</div>
                    </div>
                </div>
            </header>

            {/* Banners */}
            <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-4 space-y-3">
                {error && (
                    <div className={`${CARD_CLASS} border-rose-400/30 ring-1 ring-rose-400/10 text-rose-200 px-4 py-3 flex items-center gap-2`}>
                        <AlertTriangle className="w-4 h-4" />
                        {error}
                    </div>
                )}
                {success && (
                    <div className={`${CARD_CLASS} border-emerald-400/30 ring-1 ring-emerald-400/10 text-emerald-200 px-4 py-3`}>
                        {success}
                    </div>
                )}
            </div>

            <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6">
                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                        <div className={`${CARD_CLASS} w-full max-w-3xl max-h-[90vh] overflow-y-auto`}>
                            <div className="flex justify-between items-center p-4 border-b border-white/10">
                                <h2 className="text-lg font-semibold">
                                    {editingCiv ? 'Edit Civilization' : 'Add New Civilization'}
                                </h2>
                                <button onClick={resetForm} className="text-slate-400 hover:text-slate-200">
                                    <X size={22} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2">Civilization Name</label>
                                    <Input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., English Fast Castle"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2">JSON Data</label>
                                    <Textarea
                                        value={formData.data}
                                        onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                                        rows={16}
                                        placeholder='Paste build JSON here…'
                                        className="font-mono"
                                        required
                                    />
                                </div>

                                <div className="flex gap-2 pt-2">
                                    <Button
                                        type="submit"
                                        className="text-emerald-200 hover:bg-emerald-400/10 border-emerald-400/30"
                                    >
                                        <Save size={18} className="inline mr-1" />
                                        {editingCiv ? 'Update' : 'Create'}
                                    </Button>
                                    <Button onClick={resetForm}>Cancel</Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* View Modal */}
                {viewingCiv && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
                        {/* was: overflow-hidden; now flex column with a scrollable content area */}
                        <div className={`${CARD_CLASS} w-full max-w-4xl max-h-[90vh] flex flex-col`}>
                            <div className="flex justify-between items-center p-4 border-b border-white/10">
                                <h2 className="text-lg font-semibold">{viewingCiv.name}</h2>
                                <button
                                    onClick={() => setViewingCiv(null)}
                                    className="text-slate-400 hover:text-slate-200"
                                >
                                    <X size={22} />
                                </button>
                            </div>

                            {/* scrollable body */}
                            <div className="p-4 flex-1 min-h-0 overflow-auto">
                                <div className={`${CARD_CLASS} p-3`}>
                                    <h3 className="text-sm font-semibold mb-2 text-slate-300">Civilization Data</h3>
                                    <pre className="text-sm font-mono whitespace-pre-wrap break-words text-slate-100">
            {JSON.stringify(viewingCiv.data, null, 2)}
          </pre>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Civs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {civs.map((civ) => (
                        <div key={civ.id} className={`${CARD_CLASS} p-4 hover:bg-white/[.07] transition-colors`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {civ.data?.emoji && (
                                            <span className="text-2xl leading-none">{civ.data.emoji}</span>
                                        )}
                                        <h3 className="text-base font-semibold truncate">{civ.name}</h3>
                                    </div>
                                    {civ.data?.description && (
                                        <p className="text-slate-400 text-sm mt-2 line-clamp-3">
                                            {String(civ.data.description).replace(/\*\*/g, '')}
                                        </p>
                                    )}
                                </div>
                                <div className="shrink-0 flex gap-1">
                                    <Button onClick={() => handleView(civ)} className="px-2 py-1">
                                        <Eye size={16} />
                                    </Button>
                                    <Button onClick={() => handleEdit(civ)} className="px-2 py-1">
                                        <Edit3 size={16} />
                                    </Button>
                                    <Button
                                        onClick={() => handleDelete(civ.id)}
                                        className="px-2 py-1 text-rose-200 hover:bg-rose-400/10 border-rose-400/30"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-4 text-xs text-slate-400">
                                <div className="truncate">ID: {civ.id}</div>
                                <div>Created: {civ.created ? new Date(civ.created).toLocaleString() : '—'}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {civs.length === 0 && (
                    <div className="text-center py-16">
                        <div className={`${CARD_CLASS} inline-block px-6 py-5`}>
                            <div className="text-slate-300 text-base mb-3">No civilizations found</div>
                            <Button onClick={() => { setShowForm(true); setEditingCiv(null); }}>
                                <Plus size={18} className="inline mr-1" />
                                Add Your First Civilization
                            </Button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default CivsPage;
